import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/components/mobix/Navbar";

const EASE = [0.22, 1, 0.36, 1];

const NEWS_IMAGES = {
  government: "https://images.unsplash.com/photo-1763787002975-5aefe199191b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=640&fit=crop",
  policy: "https://images.unsplash.com/photo-1628813640648-34919e40c390?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=640&fit=crop",
  community: "https://images.unsplash.com/photo-1561489413-985b06da5bee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=640&fit=crop",
  veterans: "https://images.unsplash.com/photo-1760333334115-e75194471dd7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=640&fit=crop",
  social: "https://images.unsplash.com/photo-1772588627327-1eeddcf73c11?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=640&fit=crop",
};

export default function News() {
  const { t } = useLang();

  return (
    <section id="news" className="relative overflow-hidden bg-[#F9FAFB] py-28 lg:py-40" data-testid="news-section">
      <div className="relative z-10 max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14 lg:mb-16 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8"
        >
          <div>
            <p className="text-[13px] font-bold tracking-[0.24em] uppercase text-mobix-orange mb-6">{t.news.overline}</p>
            <h2 className="font-extrabold uppercase tracking-tight text-mobix-navy text-[clamp(1.875rem,4.8vw,3.75rem)] leading-[1.02] break-words" data-testid="news-title">
              {t.news.title}
              <span className="text-mobix-orange">.</span>
            </h2>
            <span className="mt-8 block h-[3px] w-32 bg-mobix-orange" aria-hidden="true" />
          </div>
          <p className="lg:mt-4 lg:max-w-xs text-base sm:text-lg font-medium text-mobix-navy leading-relaxed">
            {t.news.subtitle}
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 lg:gap-6">
          {t.news.items.map((item, i) => (
            <motion.article
              key={item.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: (i % 5) * 0.08 }}
              data-testid={`news-card-${item.key}`}
              className="group flex flex-col overflow-hidden rounded-3xl border border-mobix-navy/15 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:border-mobix-navy/25 hover:shadow-[0_28px_60px_rgba(11,43,86,0.14)]"
            >
              <div className="h-36 sm:h-52 w-full overflow-hidden">
                <img
                  src={NEWS_IMAGES[item.key]}
                  alt={item.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-4 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-mobix-orange">{item.category}</span>
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-wide text-mobix-navy/40 whitespace-nowrap">{item.date}</span>
                </div>
                <h3 className="mt-3 sm:mt-4 text-[15px] sm:text-[17px] font-bold leading-snug text-mobix-navy">{item.title}</h3>
                <p className="mt-2.5 sm:mt-4 flex-1 text-[13px] sm:text-[14px] leading-relaxed text-mobix-navy/60">{item.desc}</p>
                <button
                  onClick={() => scrollToId("contact")}
                  data-testid={`news-readmore-${item.key}`}
                  className="mt-4 sm:mt-6 inline-flex items-center gap-2 text-[13px] sm:text-sm font-bold text-mobix-navy"
                >
                  {t.news.readMore}
                  <ArrowRight size={16} className="text-mobix-orange transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>

        {/* View All */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
          className="mt-12"
        >
          <button
            onClick={() => scrollToId("contact")}
            data-testid="news-view-all"
            className="group inline-flex items-center gap-3 rounded-3xl border-2 border-mobix-navy px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-mobix-navy transition-all duration-300 hover:bg-mobix-navy hover:text-white"
          >
            {t.news.viewAll}
            <ArrowUpRight size={18} className="text-mobix-orange transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
